/**
 * The calculators' arithmetic.
 *
 * EVERY function here mirrors one in backend/analysis.py, line for line, and
 * tests/test_calculator_parity.py runs both over shared fixtures and asserts
 * the numbers are identical. That test is the only reason this file is allowed
 * to exist: a free calculator that disagrees with the A$990 briefing is worse
 * than no calculator, because the prospect finds the contradiction after paying.
 *
 * If you change anything here, change the Python and let the parity test prove
 * they still agree. If you change the Python, this file is already failing.
 */

// Mirrors analysis.PAID_RECONCILIATION_TOLERANCE_PCT. Above this disagreement
// between declared paid share and the paid revenue the submitted ROAS implies,
// anything computed from the zero-marketing boundary is soft and says so.
const PAID_RECONCILIATION_TOLERANCE_PCT = 20.0;

// --------------------------------------------------------------- primitives

/**
 * Python's round(), which is half-to-EVEN, not half-up like Math.round.
 * round(0.5) is 0 in Python and 1 in JavaScript, and every figure below is
 * rounded before it is compared, so this is load-bearing for parity rather
 * than pedantry.
 */
export function pyRound(x, digits = 0) {
  if (x === null || x === undefined || !isFinite(x)) return x;
  // Halves are detected from the DECIMAL EXPANSION, never by scaling. Both
  // earlier attempts failed here and the parity test caught both: multiplying
  // 777 x 8.005 (really 6219.88500000000021827) by 100 gives exactly 621988.5,
  // a half the value does not actually have - Python sees the digits past the
  // 5 and rounds up to 6219.89, while half-even on the false half gives
  // 6219.88. One cent, on a line a reader checks against their own invoice.
  const s = Math.abs(x).toFixed(digits + 18);
  const dot = s.indexOf(".");
  const tail = s.slice(dot + 1 + digits);
  const isExactHalf = tail[0] === "5" && /^0*$/.test(tail.slice(1));

  if (isExactHalf) {
    // Python rounds a true half to EVEN; toFixed rounds it away from zero.
    const f = Math.pow(10, digits);
    const lo = Math.round(Number(s.slice(0, dot + 1 + digits) || "0") * f);
    const evened = lo % 2 === 0 ? lo : lo + 1;
    return (x < 0 ? -evened : evened) / f;
  }

  // Everything else: round the exact value, which is what toFixed and Python
  // both do, and they agree there.
  return Number(x.toFixed(digits));
}

/** analysis._pct */
export function pct(n, d) {
  return d ? pyRound((100 * n) / d, 1) : 0.0;
}

/**
 * analysis._o - a submitted 0 is real data, not a missing value. A brand with
 * no marketing spend has spend 0; treating that as "not submitted" would drop
 * the line from the waterfall instead of showing it at zero.
 */
export function opt(intake, key) {
  const v = intake ? intake[key] : undefined;
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return isFinite(n) ? n : null;
}

/**
 * analysis._flag - a yes/no intake field. Absent means the DEFAULT, not false,
 * which is why this cannot be `opt()`: that returns a number and a missing flag
 * would fall back to zero.
 */
export function flag(intake, key, dflt = true) {
  const v = intake ? intake[key] : undefined;
  if (v === null || v === undefined || v === "") return dflt;
  if (typeof v === "string") {
    return !["0", "false", "no", "discarded", "written off"].includes(v.trim().toLowerCase());
  }
  return Boolean(v);
}


/** Python's %g, for labels only - never for a value. */
function g(n) {
  return String(parseFloat(Number(n).toPrecision(6)));
}

export const money = (n, cur) =>
  n === null || n === undefined || !isFinite(n)
    ? "—"
    : `${cur ? cur + " " : ""}${Number(n).toLocaleString("en-AU", {
        minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ------------------------------------------------------ the margin waterfall

/**
 * Mirrors analysis.margin_waterfall().
 *
 * A step whose inputs were not submitted is SKIPPED and recorded in `gaps`,
 * never zero-filled. Zero-filling a cost the operator did not enter would
 * overstate what they keep, and this calculator would then flatter them in
 * exactly the way the audit refuses to.
 */
export function marginWaterfall(intake) {
  const currency = ((intake.pnl_currency || "") + "").trim() || null;
  const gaps = [];
  const rows = [];

  let grossSales = opt(intake, "monthly_gross_revenue");
  const aov = opt(intake, "aov");
  const orders = opt(intake, "monthly_order_volume");
  if (grossSales === null && aov !== null && orders !== null) grossSales = aov * orders;
  if (grossSales === null) {
    gaps.push(
      "No monthly gross revenue (or AOV + order volume) submitted - the waterfall cannot be built without a top line.");
    return { currency, rows: [], checkpoints: {}, gaps };
  }

  rows.push({ key: "gross_sales", label: "Gross sales", value: pyRound(grossSales, 2), type: "total" });
  let running = grossSales;

  const returnRate = opt(intake, "return_rate_pct");
  if (returnRate !== null) {
    const refunds = pyRound((grossSales * returnRate) / 100, 2);
    rows.push({ key: "refunds", label: `Refunds on returns (${g(returnRate)}%)`, value: -refunds, type: "delta" });
    running -= refunds;
  } else {
    gaps.push("No return rate submitted - gross sales and net sales are treated as equal.");
  }
  const netSales = pyRound(running, 2);
  rows.push({ key: "net_sales", label: "Net sales", value: netSales, type: "subtotal" });

  // Charged on NET sales where a returned item goes back on the shelf, because
  // the goods are then paid for once; on GROSS sales where returns are
  // discarded, because there every order shipped costs the goods whether it
  // comes back or not. Worth 6.6 points of CM1 on a 13% return rate.
  const cogsPct = opt(intake, "cogs_pct_of_aov");
  const restocked = flag(intake, "returns_restocked", true);
  if (cogsPct !== null) {
    const cogsBase = restocked ? netSales : grossSales;
    const cogs = pyRound((cogsBase * cogsPct) / 100, 2);
    const label = restocked
      ? `COGS (${g(cogsPct)}%)`
      : `COGS (${g(cogsPct)}% of gross - returns not resold)`;
    rows.push({ key: "cogs", label, value: -cogs, type: "delta" });
    running -= cogs;
  } else {
    gaps.push("No COGS % submitted - CM1 below is net sales with no cost of goods removed, an upper bound rather than a real margin.");
  }
  const cm1 = pyRound(running, 2);
  rows.push({ key: "cm1", label: "CM1 · Gross margin", value: cm1, type: "subtotal" });

  const costPerReturn = opt(intake, "cost_per_return");
  if (returnRate !== null && costPerReturn !== null && orders !== null) {
    const handling = pyRound((orders * returnRate / 100) * costPerReturn, 2);
    rows.push({ key: "return_handling", label: "Return handling cost", value: -handling, type: "delta" });
    running -= handling;
  }
  const onward = opt(intake, "onward_shipping_cost");
  if (onward !== null && orders !== null) {
    const c = pyRound(orders * onward, 2);
    rows.push({ key: "shipping", label: "Outbound shipping", value: -c, type: "delta" });
    running -= c;
  }
  const packaging = opt(intake, "packaging_cost");
  if (packaging !== null && orders !== null) {
    const c = pyRound(orders * packaging, 2);
    rows.push({ key: "packaging", label: "Packaging", value: -c, type: "delta" });
    running -= c;
  }
  const gatewayPct = opt(intake, "payment_gateway_fee_pct");
  if (gatewayPct !== null) {
    const c = pyRound((grossSales * gatewayPct) / 100, 2);
    rows.push({ key: "gateway", label: `Payment gateway fees (${g(gatewayPct)}%)`, value: -c, type: "delta" });
    running -= c;
  }
  const threepl = opt(intake, "fulfillment_3pl_fee");
  if (threepl !== null && orders !== null) {
    const c = pyRound(orders * threepl, 2);
    rows.push({ key: "fulfilment", label: "3PL fulfillment fees", value: -c, type: "delta" });
    running -= c;
  }
  if (orders === null &&
      ["onward_shipping_cost", "packaging_cost", "fulfillment_3pl_fee"].some((k) => opt(intake, k) !== null)) {
    gaps.push("Per-order fulfillment costs were submitted without a monthly order volume, so they could not be extended to a monthly figure and are excluded below.");
  }
  const cm2 = pyRound(running, 2);
  rows.push({ key: "cm2", label: "CM2 · Fulfillment margin", value: cm2, type: "subtotal" });

  const marketing = opt(intake, "monthly_marketing_spend");
  if (marketing !== null) {
    rows.push({ key: "marketing", label: "Digital marketing", value: -pyRound(marketing, 2), type: "delta" });
    running -= marketing;
  } else {
    gaps.push("No marketing spend submitted - CM3 below does not reflect acquisition cost.");
  }
  const cm3 = pyRound(running, 2);
  rows.push({ key: "cm3", label: "CM3 · Contribution margin", value: cm3, type: "subtotal" });

  let cm4 = null;
  if (intake.sells_on_marketplaces) {
    const share = opt(intake, "marketplace_revenue_share_pct");
    const commission = opt(intake, "marketplace_commission_pct");
    const adSpend = opt(intake, "marketplace_ad_spend");
    if (share !== null && commission !== null) {
      const c = pyRound(((grossSales * share) / 100 * commission) / 100, 2);
      rows.push({ key: "marketplace_commission", label: "Marketplace commissions", value: -c, type: "delta" });
      running -= c;
    }
    if (adSpend !== null) {
      rows.push({ key: "marketplace_ads", label: "Marketplace ad spend", value: -pyRound(adSpend, 2), type: "delta" });
      running -= adSpend;
    }
    if (share !== null || adSpend !== null) {
      cm4 = pyRound(running, 2);
      rows.push({ key: "cm4", label: "CM4 · Post-marketplace margin", value: cm4, type: "subtotal" });
    }
  }

  const overhead = opt(intake, "monthly_fixed_overhead");
  if (overhead !== null) {
    rows.push({ key: "overhead", label: "Fixed overhead", value: -pyRound(overhead, 2), type: "delta" });
    running -= overhead;
  } else {
    gaps.push("No fixed overhead submitted - the figure below sits above fixed costs, not a true net profit line.");
  }
  const net = pyRound(running, 2);
  rows.push({
    key: "net",
    label: overhead !== null ? "Net profit (before owner drawings or salary)" : "Contribution before fixed overhead",
    value: net, type: "final",
  });

  return {
    currency, rows, gaps,
    checkpoints: {
      gross_sales: grossSales, net_sales: netSales,
      cm1, cm1_pct: pct(cm1, netSales),
      cm2, cm2_pct: pct(cm2, netSales),
      cm3, cm3_pct: pct(cm3, netSales),
      cm4, cm4_pct: cm4 !== null ? pct(cm4, netSales) : null,
      net, net_pct: pct(net, netSales),
      net_is_final: overhead !== null,
    },
  };
}

// --------------------------------------------------------- marketing / ROAS

/**
 * Mirrors analysis.marketing_efficiency().
 *
 * The rates are read off a waterfall that has already been built, never
 * recomputed from the raw inputs - one calculation path, so there is nothing
 * for a second path to disagree with.
 */
export function marketingEfficiency(intake, waterfall) {
  const cp = (waterfall && waterfall.checkpoints) || {};
  const grossSales = cp.gross_sales;
  const netSales = cp.net_sales;
  const cm2 = cp.cm2;
  const roas = opt(intake, "blended_roas");
  if (!grossSales || !netSales || cm2 === undefined || cm2 === null || !roas) return {};

  const cm2Rate = cm2 / netSales;
  const netOfReturns = netSales / grossSales;
  const contributionPerDollar = cm2Rate * netOfReturns * roas;
  const denom = cm2Rate * netOfReturns;
  const breakevenRoas = denom ? 1.0 / denom : null;

  const out = {
    contribution_rate_pct: pyRound(cm2Rate * 100, 1),
    net_of_returns_pct: pyRound(netOfReturns * 100, 1),
    roas,
    breakeven_roas: breakevenRoas ? pyRound(breakevenRoas, 2) : null,
    contribution_per_marketing_dollar: pyRound(contributionPerDollar, 4),
    value_per_dollar_cents: pyRound((contributionPerDollar - 1) * 100, 1),
    marketing_is_accretive: contributionPerDollar >= 1.0,
    formula: "CM2 rate x (net sales / gross sales) x blended ROAS",
  };
  if (breakevenRoas) out.roas_gap = pyRound(breakevenRoas - roas, 2);

  // What the business looks like with paid acquisition removed entirely. Not a
  // recommendation - a boundary. It answers "is marketing even the problem".
  const paidShare = opt(intake, "paid_attributed_revenue_pct");
  const overhead = opt(intake, "monthly_fixed_overhead");
  const spend = opt(intake, "monthly_marketing_spend");
  if (paidShare !== null && overhead !== null) {
    const nonPaidGross = grossSales * (1 - paidShare / 100.0);
    const nonPaidContribution = nonPaidGross * netOfReturns * cm2Rate;
    const netAtZero = nonPaidContribution - overhead;
    Object.assign(out, {
      paid_share_pct: paidShare,
      non_paid_gross: pyRound(nonPaidGross, 2),
      non_paid_contribution: pyRound(nonPaidContribution, 2),
      fixed_overhead: overhead,
      net_at_zero_marketing: pyRound(netAtZero, 2),
      overhead_uncovered_at_zero_marketing: pyRound(Math.max(0.0, -netAtZero), 2),
      breakeven_reachable_on_marketing_alone: netAtZero >= 0,
    });
    if (spend !== null && cp.net !== undefined && cp.net !== null) {
      out.gain_from_removing_all_paid = pyRound(netAtZero - cp.net, 2);
    }
    // Two measurements of paid revenue, which routinely disagree: what the
    // client's analytics attribute, and what their own ROAS implies. Everything
    // computed off the zero-marketing boundary inherits the disagreement, so it
    // is reported rather than averaged away. Mirrors analysis.py exactly - a
    // figure on a free page and the same figure in a paid briefing cannot
    // disagree, and that is enforced by tests/test_calculator_parity.py.
    if (spend !== null) {
      const implied = spend * roas;
      const declared = grossSales - nonPaidGross;
      out.paid_revenue_declared = pyRound(declared, 2);
      out.paid_revenue_implied_by_roas = pyRound(implied, 2);
      out.paid_revenue_disagreement_pct = pct(Math.abs(implied - declared),
                                              Math.max(declared, 1.0));
      out.paid_inputs_reconcile =
        out.paid_revenue_disagreement_pct <= PAID_RECONCILIATION_TOLERANCE_PCT;
    }
  }

  // The spend consistent with breakeven IF revenue is held - reported with that
  // condition attached, because without the replacement it is not reachable.
  if (overhead !== null) {
    const ceiling = cm2 - overhead;
    out.spend_ceiling_if_revenue_held = pyRound(ceiling, 2);
    out.spend_ceiling_pct_of_net_sales = pct(ceiling, netSales);
    if (spend !== null) {
      out.spend_above_ceiling = pyRound(Math.max(0.0, spend - ceiling), 2);
      out.required_cut_pct = spend ? pct(Math.max(0.0, spend - ceiling), spend) : null;
    }
  }
  return out;
}

// ------------------------------------------------------- shipping threshold

/**
 * Mirrors the computed half of analysis.shipping_threshold().
 *
 * Refuses rather than guesses. Without a real per-order shipping cost and a
 * real contribution rate, a threshold is a made-up number wearing a decimal
 * point - so it returns data_unavailable with the reason, exactly as the audit
 * does.
 */
export function shippingThreshold(shipCost, contributionRatePct, medianPrice) {
  if (!shipCost || !contributionRatePct || contributionRatePct <= 0) {
    return {
      breakeven_threshold: "data_unavailable",
      why_unavailable:
        "Needs your per-order shipping cost and contribution margin rate. Both come from the intake form; neither is inferred from the catalogue.",
    };
  }
  const m = contributionRatePct / 100.0;
  const breakeven = shipCost / m;
  const out = {
    breakeven_threshold: pyRound(breakeven, 2),
    ship_cost: pyRound(shipCost, 2),
    contribution_rate_pct: pyRound(contributionRatePct, 1),
  };
  if (medianPrice) {
    out.median_price = pyRound(medianPrice, 2);
    out.items_needed_at_median = pyRound(breakeven / out.median_price, 1);
  }
  return out;
}

// ------------------------------------------------- break-even volume, by bisection

const scaleVolume = (intake, v) => {
  const out = { ...intake };
  for (const k of ["monthly_gross_revenue", "monthly_order_volume"]) {
    const val = opt(intake, k);
    if (val !== null) out[k] = val * v;
  }
  return out;
};

/**
 * Mirrors analysis._breakeven_volume(): the percentage volume LOSS at which a
 * move stops paying.
 *
 * Solved by re-running the SAME waterfall at lower volumes and bisecting -
 * deliberately not a closed-form second formula, so it cannot drift from the
 * baseline it is measured against. Copying that method matters more than
 * copying the number.
 */
export function breakevenVolumeLossPct(intake, changes) {
  const base = marginWaterfall(intake).checkpoints || {};
  const baseNet = base.net;
  if (baseNet === undefined || baseNet === null) return null;
  const trial = { ...intake, ...changes };
  const netAt = (v) => (marginWaterfall(scaleVolume(trial, v)).checkpoints || {}).net;

  const hi = netAt(1.0);
  if (hi === undefined || hi === null || hi <= baseNet) return null;
  if ((netAt(0.3) || 0) > baseNet) return null;   // survives even a 70% loss
  let a = 0.3, b = 1.0;
  for (let i = 0; i < 40; i++) {
    const mid = (a + b) / 2;
    if ((netAt(mid) || 0) > baseNet) b = mid; else a = mid;
  }
  return pyRound((1 - (a + b) / 2) * 100, 1);
}

// ------------------------------------------------------------- per-unit maths
//
// These four have no counterpart in the audit engine, so there is no report
// figure for them to contradict. They are built from the same primitives and
// the same convention - contribution after every variable cost, never a gross
// margin dressed up as profit.

/** Contribution per unit, after every variable cost the operator entered. */
export function unitContribution({ price, unitCost, shipping, packaging, gatewayPct,
                                   returnRatePct, costPerReturn, adCostPerUnit }) {
  const p = Number(price) || 0;
  if (!p) return null;
  const parts = [];
  const add = (label, v) => { if (v) parts.push({ label, value: -pyRound(v, 2) }); };
  add("Cost of goods", Number(unitCost) || 0);
  add("Outbound shipping", Number(shipping) || 0);
  add("Packaging", Number(packaging) || 0);
  add("Payment gateway", (p * (Number(gatewayPct) || 0)) / 100);
  add("Returns provision", ((Number(returnRatePct) || 0) / 100) * (Number(costPerReturn) || 0));
  add("Acquisition", Number(adCostPerUnit) || 0);
  const contribution = pyRound(parts.reduce((acc, r) => acc + r.value, p), 2);
  return { price: pyRound(p, 2), rows: parts, contribution, contribution_pct: pct(contribution, p) };
}

/** What a discount does to contribution, and the volume it must buy back. */
export function discountImpact({ price, unitCost, discountPct }) {
  const p = Number(price) || 0;
  const c = Number(unitCost) || 0;
  const d = Number(discountPct) || 0;
  if (!p) return null;
  const m0 = p - c;
  const newPrice = pyRound(p * (1 - d / 100), 2);
  const m1 = newPrice - c;
  return {
    price: pyRound(p, 2), new_price: newPrice,
    margin_before: pyRound(m0, 2), margin_before_pct: pct(m0, p),
    margin_after: pyRound(m1, 2), margin_after_pct: pct(m1, newPrice),
    margin_lost_pct: m0 ? pyRound(((m0 - m1) / m0) * 100, 1) : null,
    // Units must rise by this much for the discounted line to earn what it did.
    volume_needed_pct: m1 > 0 ? pyRound((m0 / m1 - 1) * 100, 1) : null,
    destroys_contribution: m1 <= 0,
  };
}

/** What a price rise does, and the volume it can afford to lose. */
export function priceIncreaseImpact({ price, unitCost, increasePct }) {
  const p = Number(price) || 0;
  const c = Number(unitCost) || 0;
  const inc = Number(increasePct) || 0;
  if (!p) return null;
  const m0 = p - c;
  const newPrice = pyRound(p * (1 + inc / 100), 2);
  const m1 = newPrice - c;
  return {
    price: pyRound(p, 2), new_price: newPrice,
    margin_before: pyRound(m0, 2), margin_before_pct: pct(m0, p),
    margin_after: pyRound(m1, 2), margin_after_pct: pct(m1, newPrice),
    margin_gain_pct: m0 ? pyRound(((m1 - m0) / m0) * 100, 1) : null,
    // Sell this share fewer units and you are still no worse off.
    volume_can_lose_pct: m1 > 0 ? pyRound((1 - m0 / m1) * 100, 1) : null,
  };
}

/** A bundle against the singles it is made of. */
export function bundleMargin({ components, bundlePrice }) {
  const items = (components || []).filter((c) => Number(c.price) > 0);
  if (!items.length || !Number(bundlePrice)) return null;
  const sumPrice = items.reduce((a, c) => a + Number(c.price), 0);
  const sumCost = items.reduce((a, c) => a + (Number(c.cost) || 0), 0);
  const bp = Number(bundlePrice);
  const singlesMargin = sumPrice - sumCost;
  const bundleM = bp - sumCost;
  return {
    components: items.length,
    sum_of_singles: pyRound(sumPrice, 2),
    bundle_price: pyRound(bp, 2),
    discount_vs_singles: pyRound(sumPrice - bp, 2),
    discount_vs_singles_pct: pct(sumPrice - bp, sumPrice),
    cost_of_goods: pyRound(sumCost, 2),
    singles_margin: pyRound(singlesMargin, 2),
    singles_margin_pct: pct(singlesMargin, sumPrice),
    bundle_margin: pyRound(bundleM, 2),
    bundle_margin_pct: pct(bundleM, bp),
    margin_given_away: pyRound(singlesMargin - bundleM, 2),
    below_cost: bundleM <= 0,
  };
}
